/**
 * ToolModal Component
 * Modal to display tool content
 */

import React, { useEffect, useRef } from 'react';
import { toolsData } from '../../data/toolsData';

const ToolModal = ({ tool, onClose }) => {
  const modalRef = useRef(null);
  const toolContent = toolsData[tool.id];

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  // Handle click outside modal
  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50"
      onClick={handleBackdropClick}
    >
      <div 
        ref={modalRef}
        className="bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[80vh] overflow-hidden flex flex-col"
      >
        {/* Header */}
        <div className="bg-indigo-600 text-white px-6 py-4 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-bold">{toolContent?.name || tool.name}</h2>
            <p className="text-indigo-200 text-sm">{tool.id}</p>
          </div>
          <button
            onClick={onClose}
            className="text-white hover:text-indigo-200 transition-colors"
            aria-label="Close modal"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto flex-1">
          {toolContent ? (
            <div className="prose prose-indigo max-w-none">
              {toolContent.content.split('\n').map((line, idx) => {
                if (line.trim() === '') return null;
                // Handle numbered lists
                if (line.match(/^\d+\./)) {
                  return <p key={idx} className="mb-2 pl-4">{line}</p>;
                }
                // Handle bullet points
                if (line.startsWith('-')) {
                  return <p key={idx} className="mb-2 pl-4">• {line.substring(1).trim()}</p>;
                }
                return <p key={idx} className="mb-2">{line}</p>;
              })}
            </div>
          ) : (
            <div className="text-center py-8">
              <p className="text-gray-500">Tool content not found.</p>
              <p className="text-sm text-gray-400 mt-2">Tool ID: {tool.id}</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 px-6 py-4 bg-gray-50">
          <button
            onClick={onClose}
            className="w-full px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default ToolModal;